import React, { useEffect, useRef, useState } from "react";

/**
 * Premium Eiffel 65 Preloader.
 * - Counts 0 → 65 (the band number) tied to REAL load progress.
 * - Animated blue light rays + orbital rings.
 * - Soft fade-out when reaching 65.
 *
 * Performance contract:
 * - Minimum visible time = 800ms (visual flair, brand recognition).
 * - As soon as window load OR React hydration finishes (whichever later),
 *   the splash accelerates toward 65 and dismisses.
 * - The HTML-only #boot-shell (in public/index.html) is removed as soon as
 *   this component mounts — so the user never sees a white screen.
 */
export default function Splash({ onDone }) {
  const [pct, setPct] = useState(0);
  const [exiting, setExiting] = useState(false);
  const readyRef = useRef(false);

  // Remove the static HTML boot-shell once React renders Splash (visually
  // identical, so the handover is imperceptible).
  useEffect(() => {
    const el = document.getElementById("boot-shell");
    if (el) {
      el.classList.add("boot-gone");
      // Remove after fade completes
      setTimeout(() => { try { el.remove(); } catch { /* ignore */ } }, 400);
    }
  }, []);

  // Mark "ready" when the window has fully loaded (images, fonts, deferred bundles).
  useEffect(() => {
    const mark = () => { readyRef.current = true; };
    if (document.readyState === "complete") {
      readyRef.current = true;
    } else {
      window.addEventListener("load", mark, { once: true });
      // Hard ceiling: never wait longer than 700ms for window.load — the splash
      // must hit 65 well under ~1.2s even on slow connections.
      const hard = setTimeout(mark, 700);
      return () => { window.removeEventListener("load", mark); clearTimeout(hard); };
    }
  }, []);

  useEffect(() => {
    const start = performance.now();
    const MIN_VISIBLE = 800; // visual minimum
    let raf;
    const tick = (now) => {
      const elapsed = now - start;
      // Two phases:
      // 1) 0 → ~55 of 65 during MIN_VISIBLE (ease-out)
      // 2) ramp to 65 once we're past MIN_VISIBLE — independent of network load
      //    so the worst-case stays predictable (~1000ms).
      let p;
      if (elapsed < MIN_VISIBLE) {
        const t = elapsed / MIN_VISIBLE;
        const eased = 1 - Math.pow(1 - t, 3);
        p = eased * 55; // 0 → 55
      } else {
        // Drive home from 55 → 65 in ~120ms. The `readyRef` is checked only to
        // optionally delay if the network is *still* not idle (rare on fast
        // links — capped by the 700ms hard ceiling above).
        const since = elapsed - MIN_VISIBLE;
        const wait = readyRef.current ? 0 : Math.min(since / 200, 0.4); // tiny stall up to 80ms while waiting
        const t = Math.min((since - wait * 200) / 120, 1);
        p = 55 + Math.max(0, t) * 10;
      }
      setPct(Math.min(p, 65));
      if (p < 65) {
        raf = requestAnimationFrame(tick);
      } else {
        // Exit immediately, brief fade.
        setExiting(true);
        setTimeout(() => onDone && onDone(), 300);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [onDone]);

  return (
    <div
      data-testid="splash-screen"
      className={`splash-pro ${exiting ? "splash-pro-exit" : ""}`}
    >
      {/* Light rays / glow background */}
      <div className="splash-rays" />
      <div className="splash-orb splash-orb-1" />
      <div className="splash-orb splash-orb-2" />
      <div className="splash-grid-bg" />

      {/* Orbital rings */}
      <div className="splash-ring splash-ring-1" />
      <div className="splash-ring splash-ring-2" />
      <div className="splash-ring splash-ring-3" />

      <div className="splash-center">
        <div className="splash-pretitle">The Fan Community · Booting</div>

        <h1 className="splash-logo" data-testid="splash-logo">
          <span className="splash-logo-main">EIFFEL</span>
          <span className="splash-logo-65" data-testid="splash-progress-number">
            {Math.floor(pct)}
          </span>
        </h1>

        <div className="splash-progress-row">
          <div className="splash-progress-track">
            <div
              className="splash-progress-fill"
              data-testid="splash-progress-bar"
              style={{ width: `${(pct / 65) * 100}%` }}
            />
            <div
              className="splash-progress-glow"
              style={{ left: `${(pct / 65) * 100}%` }}
            />
          </div>
          <div className="splash-progress-pct">
            {Math.floor(pct)}<span>%</span>
          </div>
        </div>

        <div className="splash-eq">
          {Array.from({ length: 18 }).map((_, i) => (
            <span
              key={i}
              className="splash-eq-bar"
              style={{ animationDelay: `${i * 0.06}s` }}
            />
          ))}
        </div>

        <div className="splash-tagline">
          {pct < 20 && "Loading Bliss Corporation archives…"}
          {pct >= 20 && pct < 40 && "Booting Da Ba Dee protocol…"}
          {pct >= 40 && pct < 55 && "Decoding Europop signals…"}
          {pct >= 55 && pct < 65 && "Welcome to the Blue universe."}
          {pct >= 65 && "Welcome to the Blue universe."}
        </div>
      </div>
    </div>
  );
}
